import React from 'react';
import { EventPageType } from '@graphql/generated-base';

import { LazyCoupleStory } from '../../../../widgets/CoupleStory/CoupleStory.lazy';
import { LazyFaqList } from '../../../../widgets/FaqList/FaqList.lazy';
import { LazyScheduleList } from '../../../../widgets/ScheduleList/ScheduleList.lazy';
import { LazyTravelList } from '../../../../widgets/TravelList/TravelList.lazy';
import { LazyWeddingPartyList } from '../../../../widgets/WeddingPartyList/WeddingPartyList.lazy';
import { LazyTidbitsList } from '../../../../widgets/TidbitsList/TidbitsList.lazy';
import type { LayoutAlohaProps } from '../../../LayoutAloha';
import type { ColumnV2Props } from '@withjoy/joykit';
import { Box, Flex } from '@withjoy/joykit';
import { EventLogisticsBlock } from '../../../../components/EventLogisticsBlock';
import { LazyRegistryList } from '@apps/guest/packages/layout-engine/widgets/RegistryList/RegistryList.lazy';
import { CONTENT_BLOCK_MAX_WIDTH } from '../../LayoutAloha.theme';
import { CtaButtons } from '@apps/guest/packages/layout-engine/components/CtaButtons/CtaButtons';
import { LazyApps } from '@apps/guest/packages/layout-engine/widgets/Apps/Apps.lazy';
import { LazyCustom } from '@apps/guest/packages/layout-engine/widgets/Custom/Custom.lazy';
import { useEventInfo } from '@shared/utils/eventInfo';

const WelcomePageWidget = React.forwardRef<HTMLDivElement, LayoutAlohaProps>(({ event, ...restProps }, ref) => {
  const { eventInfo } = useEventInfo();
  return (
    <Box
      ref={ref}
      height={'100vh'}
      display={'flex'}
      alignItems="center"
      maxWidth={CONTENT_BLOCK_MAX_WIDTH}
      marginX="auto"
      {...restProps}
    >
      <EventLogisticsBlock
        location={event.info.location}
        date={event.info.finalizedDate}
        hideEventCountdown={eventInfo?.settings?.hideEventCountdown ?? false}
        pages={event.pages}
      >
        <CtaButtons />
      </EventLogisticsBlock>
    </Box>
  );
});

export type PreparedPages =
  | EventPageType.app
  | EventPageType.registry
  | EventPageType.travel
  | EventPageType.schedule
  | EventPageType.faq
  | EventPageType.vip
  | EventPageType.story
  | EventPageType.tidbits
  | EventPageType.welcome
  | EventPageType.custom;

export const pageColumnProps: Readonly<Record<PreparedPages, ColumnV2Props>> = {
  [EventPageType.app]: { span: [10], offset: [1] },
  [EventPageType.faq]: { span: [10, 6], offset: [1, 3] },
  [EventPageType.registry]: { span: [10], offset: [1] },
  [EventPageType.schedule]: { span: [12, 10, 8], offset: [0, 1, 2] },
  [EventPageType.story]: { span: [10, 8], offset: [1, 2] },
  [EventPageType.tidbits]: { span: [10, 8], offset: [1, 2] },
  [EventPageType.travel]: { span: [10], offset: [1] },
  [EventPageType.vip]: { span: [10, 10], offset: [1, 1] },
  [EventPageType.welcome]: {},
  [EventPageType.custom]: {},
};

export const pageWidgets: Readonly<Record<PreparedPages, React.ForwardRefExoticComponent<LayoutAlohaProps & React.RefAttributes<HTMLDivElement>>>> = {
  [EventPageType.custom]: React.forwardRef<HTMLDivElement, LayoutAlohaProps>(
    ({ event, eventHandle, isWelcomePage, isAccommodations, pageId, pageSlug, pageTitle, ...restProps }, ref) => {
      return (
        <>
          {isWelcomePage ? (
            <Box
              ref={ref}
              data-testid={'page-custom-welcome'}
              minHeight={'100vh'}
              display={'flex'}
              flexDirection={'column'}
              alignItems="center"
              justifyContent={'center'}
              maxWidth={CONTENT_BLOCK_MAX_WIDTH}
              marginX="auto"
              width={'100%'}
              {...restProps}
            >
              <LazyCustom
                pageId={pageId || ''}
                pageSlug={pageSlug || ''}
                eventId={event.id}
                pageTitle={pageTitle}
                eventHandle={eventHandle}
                pages={event.pages}
              />
            </Box>
          ) : isAccommodations ? (
            <Flex
              ref={ref}
              data-testid={'page-custom'}
              alignItems="center"
              justifyContent={'center'}
              maxWidth={CONTENT_BLOCK_MAX_WIDTH}
              width={'100%'}
              marginX="auto"
            >
              <LazyCustom
                pageId={pageId || ''}
                pageSlug={pageSlug || ''}
                eventId={event.id}
                pageTitle={pageTitle}
                eventHandle={eventHandle}
                pages={event.pages}
              />
            </Flex>
          ) : (
            <Box
              ref={ref}
              data-testid={'page-custom'}
              display={'flex'}
              alignItems="center"
              margin={'0 auto'}
              justifyContent={'center'}
              maxWidth={CONTENT_BLOCK_MAX_WIDTH}
              paddingTop={{ _: 7, sm2: 10 }}
              paddingBottom={{ _: 0, sm2: 10 }}
            >
              <LazyCustom
                pageId={pageId || ''}
                pageSlug={pageSlug || ''}
                eventId={event.id}
                pageTitle={pageTitle}
                eventHandle={eventHandle}
                pages={event.pages}
              />
            </Box>
          )}
        </>
      );
    },
  ),
  [EventPageType.app]: React.forwardRef<HTMLDivElement, LayoutAlohaProps>((_props, ref) => (
    <Box
      ref={ref}
      data-testid={'page-apps'}
      display={'flex'}
      alignItems="center"
      margin={'0 auto'}
      justifyContent={'center'}
      maxWidth={CONTENT_BLOCK_MAX_WIDTH}
      paddingTop={{ _: 7, sm2: 10 }}
      paddingBottom={{ _: 7, sm2: 10 }}
    >
      <LazyApps data-testid={'page-app'} />
    </Box>
  )),
  [EventPageType.faq]: React.forwardRef<HTMLDivElement, LayoutAlohaProps>((props, ref) => (
    <LazyFaqList
      ref={ref}
      data-testid={'page-faq'}
      eventHandle={props.eventHandle}
      margin={'0 auto'}
      maxWidth={CONTENT_BLOCK_MAX_WIDTH}
      paddingTop={{ _: 7, sm2: 10 }}
      paddingBottom={{ _: 0, sm2: 10 }}
    />
  )),
  [EventPageType.registry]: React.forwardRef<HTMLDivElement, LayoutAlohaProps>((props, ref) => (
    <LazyRegistryList
      ref={ref}
      eventId={props.event.id}
      eventHandle={props.eventHandle}
      margin={'0 auto'}
      maxWidth={CONTENT_BLOCK_MAX_WIDTH}
      paddingTop={{
        _: 7,
        lg: 56,
      }}
    />
  )),
  [EventPageType.schedule]: React.forwardRef<HTMLDivElement, LayoutAlohaProps>((props, ref) => (
    <LazyScheduleList
      ref={ref}
      data-testid={'page-schedule'}
      eventHandle={props.eventHandle}
      variant={'stack'}
      eventId={props.event.id}
      margin={'0 auto'}
      maxWidth={CONTENT_BLOCK_MAX_WIDTH}
    />
  )),
  [EventPageType.story]: React.forwardRef<HTMLDivElement, LayoutAlohaProps>((props, ref) => (
    <LazyCoupleStory
      ref={ref}
      data-testid={'page-story'}
      stackByDefault={true}
      margin={'0 auto'}
      maxWidth={CONTENT_BLOCK_MAX_WIDTH}
      paddingTop={{ _: 7, sm2: 10 }}
      paddingBottom={{ _: 0, sm2: 10 }}
      eventHandle={props.eventHandle}
      eventPages={props.event.pages}
    />
  )),
  [EventPageType.tidbits]: React.forwardRef<HTMLDivElement, LayoutAlohaProps>((props, ref) => (
    <LazyTidbitsList
      ref={ref}
      data-testid={'page-tidbits'}
      eventHandle={props.eventHandle}
      stackByDefault={true}
      margin={'0 auto'}
      maxWidth={CONTENT_BLOCK_MAX_WIDTH}
      paddingTop={{ _: 7, sm2: 10 }}
      paddingBottom={{ _: 0, sm2: 10 }}
    />
  )),
  [EventPageType.travel]: React.forwardRef<HTMLDivElement, LayoutAlohaProps>((props, ref) => (
    <LazyTravelList
      ref={ref}
      data-testid={'page-travel'}
      eventId={props.event.id}
      eventHandle={props.eventHandle}
      useMiniNav={false}
      pages={props.event.pages}
      condensed={true}
      margin={'0 auto'}
      maxWidth={CONTENT_BLOCK_MAX_WIDTH}
      paddingTop={{ _: 7, sm2: 10 }}
      paddingBottom={{ _: 0, sm2: 10 }}
    />
  )),
  [EventPageType.vip]: React.forwardRef<HTMLDivElement, LayoutAlohaProps>((props, ref) => (
    <LazyWeddingPartyList
      eventId={props.event.id}
      eventHandle={props.eventHandle}
      ref={ref}
      data-testid={'page-vip'}
      graphicAccent={undefined}
      columnCount={6}
      margin={'0 auto'}
      maxWidth={CONTENT_BLOCK_MAX_WIDTH}
      paddingTop={{ _: 7, sm2: 10 }}
      paddingBottom={{ _: 0, sm2: 10 }}
    />
  )),
  [EventPageType.welcome]: WelcomePageWidget,
};
